import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useApplicationContext } from '../../Context/applicationContext';
import { UploadDocumentContextProvider } from '../../Context/uploadDocumentContext';
import {
  getApplicationByApplicationId,
  getDocuments,
  getFeeDetails,
  getFormByFormId,
} from '../../Utils/apiServices';
import { getVisibleSteps } from '../../Utils/Helpers/applicationHelper';
import CustomizedSteppers from '../KenApplicationForm/Components/KenStepperHeader';
import KenApplicationFormHeader from '../KenApplicationFormHeader';
import Stepper from './Stepper';
import { Container } from '@mui/material';
import InstructionsStep from './InstructionsStep';
import { AuthContext } from '../../Context/authContext';
import FormHeader from '../../Containers/ApplicationForm/Components/FormHeader';
import { useTranslation } from 'react-i18next';
import { commonJson } from '../../Utils/Helpers/defaultTemplate';
import KenLoader from '../KenLoader';
import { ROMAN_MAP } from '../../Constants/constant';
import {
  FORM_VALUES_LANGUAGE,
  FORM_VALUES_NATIONALITY,
} from '../../Utils/Helpers/formValueHelpers';

export default function KenApplication() {
  const navigate = useNavigate();
  const formRef = useRef();
  const { t } = useTranslation();

  const urlSearch = useLocation().search;

  const {
    state: { user },
  } = useContext(AuthContext);

  const { applicationState, applicationDispatch } = useApplicationContext();
  const {
    formId,
    applicationId,
    applicationData,
    form,
    steps = [],
    activeStep,
    showHeader,
  } = applicationState;

  const [loader, setLoader] = useState(false);
  const [customForm, setCustomForm] = useState();

  const [isDataFetched, setDataFetched] = useState(false);
  useEffect(() => {
    if (steps[activeStep] && applicationId && applicationData && form) {
      setDataFetched(true);
    }
  }, [steps, activeStep, applicationId, applicationData, form]);

  //update context state
  const updateState = (payload) => {
    applicationDispatch({ type: 'update', payload });
  };

  const goToDashboard = () => {
    navigate('/home');
  };

  const alertMessage = (message) => {
    console.log('alert:', message);
  };

  // update applicant id to context
  useEffect(() => {
    updateState({ applicantId: user?.applicantId });
  }, [user]);

  // get formid and applicationid from query
  useEffect(() => {
    const query = new URLSearchParams(urlSearch);
    const tempId = query.get('formId');

    if (tempId && tempId !== 'null' && tempId !== 'undefined') {
      updateState({ formId: tempId });
    } else {
      alertMessage('Invalid formId. Try again');
      goToDashboard();
    }

    const id = query.get('applicationId');
    if (id) {
      updateState({ applicationId: id });
    }
  }, [urlSearch]);

  const getCampuses = (campuses = []) => {
    const data = [];
    campuses.forEach((el) => {
      if (el.combinationId && el.combinationName) {
        data.push({ label: el?.combinationName, value: el?.combinationId });
      }
    });
    return [...data];
  };

  const getClassNumber = (label) => {
    const parts = label.split(' ');
    const roman = parts[1];
    return ROMAN_MAP[roman];
  };

  // actions to be performed if formId
  useEffect(() => {
    if (!formId) {
      return;
    }

    getFormByFormId(formId)
      .then((res) => {
        // if success update context else throw alert to refresh
        if (res?.success) {
          //Extract only steps to be used
          const steps = getVisibleSteps(res?.data?.formMetadata);
          // steps.splice(0, 0, commonJson);
          // upodate context with formOptions from form data
          // NOTE: campuses, specializations are had coded picklist values from academic applications. Update formOptions if any picklist values are to be called using api

          // Adding Programs based on departmentID as key
          const customProgram = {};
          res?.data?.programs?.forEach((programs) => {
            if (Object.prototype.hasOwnProperty(programs?.departmentId)) {
              customProgram[programs?.departmentId] = [
                { value: programs?.programId, label: programs?.programName },
              ];
            } else {
              if (!customProgram[programs?.departmentId]) {
                customProgram[programs?.departmentId] = [];
              }
              customProgram[programs?.departmentId].push({
                value: programs?.programId,
                label: programs?.programName,
              });
            }
          });
          Object.keys(customProgram).forEach((items) => {
            customProgram[items].sort((a, b) => {
              const numA = getClassNumber(a.label);
              const numB = getClassNumber(b.label);
              if (numA && numB) {
                return numA - numB;
              } else {
                return a.label < b.label ? -1 : a.label > b.label ? 1 : 0;
              }
            });
          });
          setCustomForm(res?.data);
          updateState({
            form: { ...res?.data },
            steps,
            showHeader: true,
            totalSteps: steps.length,
            ...(applicationId ? {} : { activeStep: 0 }),
            formOptions: {
              campuses: getCampuses(res?.data?.campuses),
              specializations: res?.data?.specializations?.map((s) => ({
                label: s?.specializationName,
                value: s?.recordId,
              })),
              language: [...FORM_VALUES_LANGUAGE],
              nationality: [...FORM_VALUES_NATIONALITY],
              programs: Object.keys(customProgram)
                ?.map((items) => {
                  const getId = res?.data?.programs?.find((program) => {
                    return program?.departmentId === items;
                  });
                  return {
                    label: getId?.departmentName,
                    value: items,
                  };
                })
                .sort((a, b) => {
                  return a.label < b.label ? -1 : a.label > b.label ? 1 : 0;
                }),
              ...customProgram,
            },
          });
        } else {
          alertMessage('Refresh and try again');
        }
      })
      .catch((err) => {
        const { reponse: { status } = {} } = err;
        if (status === 400) {
          alertMessage('Invalid form Try again');
        }
      });
  }, [formId]);

  //actions to be performed if applicationId
  useEffect(() => {
    if (!applicationId || !customForm) {
      return;
    }

    getApplicationByApplicationId(applicationId)
      .then((res) => {
        // if success update context else throw alert to refresh
        if (res?.success && res?.data) {
          let formattedData = { ...res.data };
          const getProgramId = customForm?.programs?.find((items) => {
            return (
              items?.programPlanName ===
              formattedData['application'].appliedProgram
            );
          });
          formattedData['application'].appliedProgram = getProgramId
            ? getProgramId?.departmentId
            : formattedData['application']?.appliedProgram
            ? formattedData['application']?.appliedProgram
            : null;
          updateState({
            applicationData: formattedData,
            activeStep: parseInt(res?.data?.application?.currentStep) || 0,
            admissionInitiatedBy:
              res?.data?.application?.admissionProcessIsInitiatedBy,
            formalDeclaration:
              res?.data?.application?.iAgree === 'true' ? true : false,
          });
        }
      })
      .catch((err) => {
        const { response: { status, data: { errorMessages = [] } = {} } = {} } =
          err;
        if (status === 400 && errorMessages?.length > 0) {
          //check if invalid application
          const errObj = errorMessages.find(
            (el) => (el.errorMessage = 'Wrong application Id.')
          );
          if (errObj) {
            goToDashboard();
            alertMessage(t('message:Application_Invalid'));
          }
        }
      });
  }, [applicationId, customForm]);

  // Document Details
  useEffect(() => {
    if (!applicationState.applicantId || !applicationId) {
      return;
    }
    setLoader(true);
    getDocuments(applicationState.applicantId)
      .then((res) => {
        const getDocuments = res?.data?.find((items) => {
          return items?.applicationId === applicationId;
        });
        updateState({
          documents: getDocuments.documents,
        });
        setLoader(false);
      })
      .catch((err) => {
        setLoader(false);
        console.log(err);
      });
  }, [
    applicationState.applicantId,
    applicationId,
    applicationState.documentTrigger,
  ]);

  // Fee details
  useEffect(() => {
    if (!applicationState.applicantId || !applicationId) {
      return;
    }
    setLoader(true);
    getFeeDetails(applicationState.applicantId)
      .then((res) => {
        const getFeeDetails =
          res?.data?.find((items) => {
            return items?.applicationId === applicationId;
          }) || {};
        if (Object.keys(getFeeDetails).length > 0) {
          updateState({ feeDetails: { ...getFeeDetails?.feeDetailsList[0] } });
        }
        setLoader(false);
      })
      .catch((err) => {
        console.error(err);
        setLoader(false);
      });
  }, [applicationState.applicantId, applicationId]);

  return (
    <div>
      {loader && <KenLoader />}
      <FormHeader />
      {showHeader && (
        <KenApplicationFormHeader
          programName={form?.formName}
          duration={form?.courseDuration}
          batch={form?.courseBatch}
          endDate={form?.endDate}
        />
      )}
      {/* Add condition when application data is fetched */}
      {/* Render instruction for first time when no application id found */}
      {!applicationId && form && <InstructionsStep />}
      {isDataFetched && (
        <>
          <CustomizedSteppers
            activeStep={
              applicationData?.application?.applicationStatus === 'Submitted'
                ? steps.length
                : activeStep
            }
            steps={steps}
          />
          <UploadDocumentContextProvider>
            <Stepper step={steps[activeStep]} formRef={formRef} />
          </UploadDocumentContextProvider>
        </>
      )}
      {((applicationId && !applicationData) || !form) && (
        <Container maxWidth="md" style={{ textAlign: 'start' }}>
          <Skeleton
            width="40%"
            height="30px"
            style={{ marginTop: '50px', marginBottom: '20px' }}
          />
          <Skeleton
            count={2}
            inline
            width="45%"
            wrapper={({ children }) => (
              <span style={{ marginRight: '0.5rem' }}>{children}</span>
            )}
          />
          <Skeleton
            width="40%"
            height="30px"
            style={{ marginTop: '50px', marginBottom: '20px' }}
          />
          <Skeleton
            count={2}
            inline
            width="45%"
            wrapper={({ children }) => (
              <span style={{ marginRight: '0.5rem' }}>{children}</span>
            )}
          />
        </Container>
      )}
    </div>
  );
}
